import { TranslationKey } from './../../../../types/translation/TranslationKey';
import { itemsContainText } from '../../../../util/string.util';
import React from 'react';
import { Bin } from '../../../../types/bin/bin';
import { BinStatus } from '../../../../types/bin/binStatus';
import { t } from '../../../../types/translation/Translator';
import { BinIcon } from '../../../icons/Bin/BinIcon';
import { FilterProps, FilterStorageKey } from '../common/filter.util';
import { BinFilterDropdownContent, BinFilterInnerContent } from './DropdownContent';
import { StockLocationFilter } from '../StockLocationFilter';
import { BinStatusFilter } from './BinStatusFilter';
import { BaseFilter, BaseFilterProps } from '../common/BaseFilter';
import { TagRelation } from '../../../../types/tag/tagRelation';
import { TagFilter } from '../TagFilter/TagFilter';
import { CustomFieldFilter } from '../CustomFieldFilter';
import { CustomField, CustomFieldEntityType } from '../../../../types/custom_field/customField';
import { EntityType } from '../../../../types/comment';
import { Company } from '../../../../types/company';
import { CompanyPartnerFilter } from '../CompanyPartnerFilter/CompanyPartnerFilter';
import { StockLocation } from '../../../../types/stock_location';

export class BinFilter extends BaseFilter<Bin> {
  binStatuses: Map<string, BinStatus>;
  tagRelations: Map<string, TagRelation>;
  customFields: Map<string, CustomField>;
  bins: Map<string, Bin>;
  company: Company;
  stockLocations: Map<string, StockLocation>;

  constructor(
    binStatuses: Map<string, BinStatus>,
    tagRelations: Map<string, TagRelation>,
    customFields: Map<string, CustomField>,
    bins: Map<string, Bin>,
    company: Company,
    stockLocations: Map<string, StockLocation>,
  ) {
    super(FilterStorageKey.BIN);
    this.binStatuses = binStatuses;
    this.tagRelations = tagRelations;
    this.customFields = customFields;
    this.bins = bins;
    this.company = company;
    this.stockLocations = stockLocations;
  }

  filterOptions(): BaseFilter<any>[] {
    return [
      ...(this.company.partner ? [new CompanyPartnerFilter()] : []),
      new StockLocationFilter(this.tagRelations, this.company),
      new BinFilter(
        this.binStatuses,
        this.tagRelations,
        this.customFields,
        this.bins,
        this.company,
        this.stockLocations,
      ),
      new BinStatusFilter(),
      new TagFilter(EntityType.stockLocation),
      ...CustomFieldFilter.for(CustomFieldEntityType.bin, this.bins, this.customFields),
    ];
  }

  toLabel(): string {
    return t(TranslationKey.bin).singular.label;
  }

  toCount(props: BaseFilterProps): number {
    return props.filterProps.bin?.size || 0;
  }

  toIcon(): React.JSX.Element {
    return <BinIcon className={this.className} />;
  }

  toDropdownContent(props: BaseFilterProps): React.JSX.Element {
    return (
      <BinFilterDropdownContent filter={this} filterProps={props.filterProps} setFilterProps={props.setFilterProps} />
    );
  }

  toInnerContent(props: BaseFilterProps): string | JSX.Element {
    return (
      <BinFilterInnerContent filter={this} filterProps={props.filterProps} setFilterProps={props.setFilterProps} />
    );
  }

  search(item: Bin, query: string) {
    return itemsContainText(query, [item.name, this.binStatuses.get(item.binStatusId)?.status]);
  }

  filteredItems(items: Bin[], query: string, filterProps: FilterProps) {
    return items.filter(item => {
      return [
        StockLocationFilter.filter(filterProps, this.stockLocations.get(item.stockLocationId)),
        TagFilter.filterBy(filterProps, EntityType.stockLocation, this.tagRelations, item.stockLocationId),
        BinFilter.filter(filterProps, item, this.customFields),
        this.search(item, query),
      ].every(value => value === null || value);
    });
  }

  static filter(filterProps: FilterProps, item?: Bin, customFields: Map<string, CustomField> = new Map()) {
    if (item && filterProps.binStatus?.size && !filterProps.binStatus.has(item.binStatusId)) return false;
    if (item && filterProps.bin?.size && !filterProps.bin.has(item.id)) return false;
    if (item && filterProps.company?.size && !filterProps.company.has(item.companyId)) return false;

    return CustomFieldFilter.filter(filterProps, item, customFields, CustomFieldEntityType.bin);
  }
}
